import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Cookie } from 'lucide-react';
import { COMPANY_LEGAL as L, openCookiePreferences } from '../data/legal';

const Section = ({ title, children }) => (
  <section className="space-y-2">
    <h2 className="text-lg font-bold text-slate-800">{title}</h2>
    <div className="text-sm text-slate-600 space-y-2 leading-relaxed">{children}</div>
  </section>
);

export default function PrivacyRecruitmentPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-slate-50" data-testid="privacy-page">
      <div className="bg-[#1a4b8c] text-white">
        <div className="max-w-3xl mx-auto px-4 py-6 flex items-center gap-3">
          <img src="/logo.svg" alt="Metal Covering" className="h-10 w-10 shrink-0 brightness-0 invert" />
          <div><h1 className="text-2xl font-bold">Informativa Privacy &amp; Cookie</h1><p className="text-sm text-blue-100">Candidati – Campagna Assunzioni</p></div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        <Button variant="ghost" onClick={() => navigate('/lavora-con-noi')} data-testid="privacy-back"><ArrowLeft size={15} className="mr-1" /> Torna alle posizioni</Button>

        <p className="text-xs text-slate-400">Ultimo aggiornamento: {L.lastUpdate} · Versione consenso {L.consentVersion}</p>

        <p className="text-sm text-slate-600">La presente informativa è resa ai sensi degli artt. 13-14 del Regolamento (UE) 2016/679 (GDPR) e del D.Lgs. 196/2003 e s.m.i. ai candidati che inviano la propria candidatura tramite la sezione "Lavora con noi" di {L.name}.</p>

        <Section title="1. Titolare del trattamento">
          <p><b>{L.name}</b> – Sede legale: {L.address} – P.IVA/C.F. {L.vat} – Codice SDI {L.sdi}.</p>
          <p>Contatti: e-mail {L.email} · tel. {L.phone}.</p>
          <p>Per l'esercizio dei diritti in materia di privacy: <a className="text-[#1a4b8c] underline" href={`mailto:${L.privacyEmail}`}>{L.privacyEmail}</a>.</p>
        </Section>

        <Section title="2. Responsabile della Protezione dei Dati (DPO)">
          <p>{L.dpo} – e-mail <a className="text-[#1a4b8c] underline" href={`mailto:${L.dpoEmail}`}>{L.dpoEmail}</a>.</p>
        </Section>

        <Section title="3. Dati trattati">
          <p>Trattiamo i dati che ci fornisci nel modulo di candidatura e nei documenti allegati:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>dati anagrafici e di contatto (nome, cognome, e-mail, telefono, comune, provincia);</li>
            <li>informazioni professionali (esperienze, attestati, corsi, competenze, disponibilità, patenti);</li>
            <li>curriculum vitae, eventuale fotografia e documenti/attestati caricati;</li>
            <li>risposte al questionario di selezione;</li>
            <li>dati tecnici di navigazione e di provenienza della campagna (parametri UTM, referrer), se acconsentito.</li>
          </ul>
          <p>Ti invitiamo a non inserire dati particolari (es. dati sanitari) se non strettamente necessari; eventuali dati relativi all'idoneità sanitaria sono trattati solo nelle fasi successive e nei limiti di legge.</p>
        </Section>

        <Section title="4. Finalità e base giuridica">
          <ul className="list-disc list-inside space-y-1">
            <li>gestione della candidatura e del processo di selezione – base giuridica: misure precontrattuali su tua richiesta (art. 6.1.b) ed esecuzione di obblighi in materia di lavoro;</li>
            <li>valutazione del profilo e formazione della graduatoria interna – legittimo interesse del Titolare alla selezione del personale (art. 6.1.f);</li>
            <li>eventuale conservazione per future opportunità – sulla base del tuo consenso (art. 6.1.a), revocabile in ogni momento;</li>
            <li>cookie/statistiche e misurazione campagne – solo previo tuo consenso (vedi sezione Cookie).</li>
          </ul>
          <p><b>Il consenso al trattamento per la candidatura è necessario per poter valutare il tuo profilo; il consenso ai cookie di marketing/analitici è facoltativo e indipendente: puoi candidarti anche rifiutandolo.</b></p>
        </Section>

        <Section title="5. Conferimento dei dati">
          <p>Il conferimento dei dati necessari alla candidatura è facoltativo, ma il rifiuto rende impossibile la valutazione del profilo.</p>
        </Section>

        <Section title="6. Destinatari">
          <p>I dati sono trattati dal personale autorizzato del Titolare (HR, responsabili di selezione) e possono essere comunicati a fornitori di servizi IT/hosting nominati Responsabili del trattamento (art. 28 GDPR). I dati non sono diffusi. In caso di assunzione, i dati confluiscono nel rapporto di lavoro.</p>
        </Section>

        <Section title="7. Trasferimento extra UE">
          <p>I dati sono trattati all'interno dell'Unione Europea. Eventuali trasferimenti verso Paesi terzi avverranno solo in presenza di garanzie adeguate ai sensi degli artt. 44 e ss. del GDPR.</p>
        </Section>

        <Section title="8. Periodo di conservazione">
          <p>I dati dei candidati sono conservati per il tempo necessario alla selezione e, salvo diverso consenso, per un massimo di 24 mesi dalla ricezione della candidatura, dopodiché sono cancellati o anonimizzati. In caso di assunzione si applicano i termini previsti dalla normativa lavoristica.</p>
        </Section>

        <Section title="9. Diritti dell'interessato">
          <p>Puoi esercitare in ogni momento i diritti di accesso, rettifica, cancellazione, limitazione, opposizione e portabilità (artt. 15-22 GDPR), nonché revocare i consensi prestati, scrivendo a <a className="text-[#1a4b8c] underline" href={`mailto:${L.privacyEmail}`}>{L.privacyEmail}</a>. Hai inoltre il diritto di proporre reclamo al Garante per la protezione dei dati personali (www.garanteprivacy.it).</p>
        </Section>

        <Section title="10. Cookie">
          <p>Il sito utilizza cookie <b>tecnici/necessari</b> (indispensabili al funzionamento e all'invio della candidatura, non richiedono consenso) e, previo tuo consenso, cookie <b>analitici</b> (statistiche pseudonime, es. Google Analytics) e di <b>marketing</b> (misurazione delle campagne, es. Google Ads, Meta, LinkedIn). Nessun cookie analitico o di marketing viene attivato prima del consenso. Puoi modificare o revocare le tue scelte in qualsiasi momento.</p>
          <p><Button size="sm" variant="outline" onClick={openCookiePreferences} data-testid="open-cookie-prefs"><Cookie size={14} className="mr-1" /> Gestisci preferenze cookie</Button></p>
        </Section>

        <Section title="11. Processi decisionali automatizzati">
          <p>Il punteggio/graduatoria generato dal sistema costituisce un supporto alla valutazione: la decisione finale sulla selezione è sempre presa da personale umano. Non vengono adottate decisioni esclusivamente automatizzate con effetti giuridici sull'interessato ai sensi dell'art. 22 GDPR.</p>
        </Section>
      </div>
    </div>
  );
}
